import React, { useState } from "react";
import { sbSetSetting, sbGetSetting } from "../utils/supabaseClient";
import "../styles/MetadataForm.css";

const EMOJI_LIST = ["📚","📖","📕","📗","📘","✏️","📝","📄","📃","📑","🎓","🏫","👨‍🎓","👩‍🎓","🧑‍🏫","🎯","🎪","🎨","🎭","🎬","💡","💭","🧠","⚡","✨","🔥","💫","⭐","🌟","🌠","🔬","🔭","🧪","🧬","⚗️","🧩","🧮","📐","📏","📊","📈","📉","💹","💱","💲","💰","🏆","🥇","🥈","🥉","🏅","👑","💎","💍","🔑","🔐","🔒","🔓","🛡️","⚔️","🗡️","🔨","🔧","⚙️","🛠️","⛏️","🔩","⚒️","🪛","💻","⌨️","🖥️","🖨️","📱","📲","☎️","📞","📟","📠","🔉","🔊","📢","📣","📯","🔔","🔕","📻","📺","📷","📸","🎥","🎬","📹","🎞️","📽️","🎤","🎧","🎼","🎵","🎶","🪘","🥁","🎸","🎹","🎺","🎷","🎻","🚀","🛸","🛰️","🌌","🌃","🌆","🌇","🏙️","🌉","🌁","🗼","🗽","🗿","🏔️","⛰️","🌋","🗻","🏕️","⛺","🏠","🏡","🏘️","🏚️","🏗️","🏭","🏢","🏬","🏣","🏤","🏥","🏦","🏨","🏪","🏫","🏩","💼","👔","👗","👘","👚","👕","👖","🧥","🧤","🧣","👜","👝","👛","👒","🎩","⛑️","🌐","🔗","📡","🔀","🔁","🔂","🔄","🔃","🔆","🔅","⌚","📦","📫","📪","📬","📭","📮","📯","📇","🗃️","🗳️","🗄️","📋","📁","📂","🗂️","🗞️","📰","📓","📔","📒","📙","🧷","🧴","🧹","🧺","🧻","🧼","🧽","📿","💈","🔭","🔬","🧯","🛢️","💸","💵","💴","💶","💷","💳","🧾","✉️","📩","📨","📤","📥","🏷️","🧳","⚖️","🧰","⚙️","🗜️","🔫","🪃","🧱","⛓️","🧲","🗝️","🚪"];

const MetadataForm = ({ sections, onSave, onCancel }) => {
  const [title, setTitle] = useState("");
  const [duration, setDuration] = useState("5 min");
  const [tag, setTag] = useState("");
  const [icon, setIcon] = useState("📄");
  const [subtitle, setSubtitle] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");

    if (!title.trim()) {
      setError("Please enter a lesson title");
      return;
    }

    setSaving(true);

    try {
      // Create the lesson object (as draft - only publish sends notifications)
      const newLesson = {
        id: `lesson_${Date.now()}`,
        dur: duration,
        tag: tag,
        body: sections,
        icon: icon,
        title: title.trim(),
        addedAt: new Date().toISOString(),
        subtitle: subtitle.trim(),
        draft: true, // Save as draft - must be published to send notifications
      };

      // Get existing lessons from app_settings
      let lessons = await sbGetSetting("origamic-lessons");
      if (!Array.isArray(lessons)) {
        lessons = [];
      }

      // Add new lesson
      lessons.push(newLesson);

      // Save back to app_settings
      await sbSetSetting("origamic-lessons", lessons);

      // Notify parent
      onSave(newLesson);
    } catch (err) {
      console.error("Error saving lesson:", err);
      setError("Failed to save lesson. Try again.");
    }

    setSaving(false);
  };

  return (
    <div className="metadata-overlay">
      <div className="metadata-modal">
        <h2>Lesson Metadata</h2>
        <p className="metadata-subtitle">Add details before saving</p>

        <form onSubmit={handleSave}>
          {/* Title */}
          <div className="form-group">
            <label>Lesson Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Introduction to AI"
              disabled={saving}
            />
          </div>

          {/* Icon with Emoji Picker */}
          <div className="form-group">
            <label>Icon</label>
            <div className="emoji-picker-container">
              <div className="emoji-display">{icon || "?"}</div>
              <input
                type="text"
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                placeholder="Paste emoji"
                className="emoji-input"
                disabled={saving}
              />
              <button
                type="button"
                className="emoji-toggle-btn"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                disabled={saving}
                title="Pick emoji"
              >
                {showEmojiPicker ? "✕" : "▼"}
              </button>
            </div>
            {showEmojiPicker && (
              <div className="emoji-grid">
                {EMOJI_LIST.map((e) => (
                  <div
                    key={e}
                    className={`emoji-item ${icon === e ? "selected" : ""}`}
                    onClick={() => {
                      setIcon(e);
                      setShowEmojiPicker(false);
                    }}
                  >
                    {e}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Duration */}
          <div className="form-group">
            <label>Duration</label>
            <input
              type="text"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="5 min"
              disabled={saving}
            />
          </div>

          {/* Tag */}
          <div className="form-group">
            <label>Tag/Category</label>
            <input
              type="text"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="e.g., Strategy, Governance"
              disabled={saving}
            />
          </div>

          {/* Subtitle */}
          <div className="form-group">
            <label>Subtitle/Description</label>
            <textarea
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="Brief description of the lesson"
              rows="3"
              disabled={saving}
            />
          </div>

          {/* Preview */}
          <div className="preview-box">
            <h3>Preview</h3>
            <div className="preview-item">
              <span className="preview-icon">{icon}</span>
              <div className="preview-content">
                <div className="preview-title">{title || "Lesson Title"}</div>
                <div className="preview-meta">{tag && `${tag} • `}{duration}</div>
                <div className="preview-subtitle">
                  {subtitle || "Add a subtitle..."}
                </div>
              </div>
            </div>
            <div className="preview-sections">
              <strong>Sections ({sections.length}):</strong>
              <ul>
                {sections.slice(0, 3).map((section, i) => (
                  <li key={i}>
                    [{section[0]}] {String(section[1]).substring(0, 50)}...
                  </li>
                ))}
                {sections.length > 3 && <li>... and {sections.length - 3} more</li>}
              </ul>
            </div>
          </div>

          {error && <div className="error">{error}</div>}

          {/* Actions */}
          <div className="form-actions">
            <button
              type="submit"
              className="primary"
              disabled={saving || !title.trim()}
            >
              {saving ? "Saving..." : "Save Lesson"}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MetadataForm;
